"use client";

import { useAuth } from "@/contexts/auth-context";
import { SidebarProvider } from "@/components/ui/sidebar";
import AppSidebar from "@/components/layout/app-sidebar";
import Header from "@/components/layout/header";
import MainPanel from "@/components/layout/main-panel";
import { Loader2 } from "lucide-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

interface ConditionalLayoutProps {
  children: React.ReactNode;
  defaultSidebarOpen: boolean;
}

const queryClient = new QueryClient();

export default function ConditionalLayout({
  children,
  defaultSidebarOpen,
}: ConditionalLayoutProps) {
  const { isLoading } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // For authenticated users, show the full layout with sidebar and header
  return (
    <QueryClientProvider client={queryClient}>
      <SidebarProvider defaultOpen={defaultSidebarOpen}>
        <div className="flex w-full bg-background">
          <AppSidebar />
          <MainPanel>
            <Header />
            <main className="flex-1 p-4 md:p-6 lg:p-8 bg-muted/30">
              {children}
            </main>
          </MainPanel>
        </div>
      </SidebarProvider>
    </QueryClientProvider>
  );
}
